import { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Heart, MessageCircle, ChevronRight, Utensils, Dumbbell, Scale, Trophy, Sparkles, Navigation, Clock, Flame } from 'lucide-react';
import { API_URL } from '../config/api';
import RouteMap from '../components/RouteMap';
import PostImage from '../components/PostImage';

// Load Leaflet on demand — same pattern used by Camminata.js. We only
// load it once per session to keep the public share page lightweight
// for users who land on a post that doesn't include a route (meal,
// weight update, ...).
const ensureLeafletLoaded = () => {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.L) return Promise.resolve();
  return new Promise((resolve) => {
    if (!document.querySelector('link[href*="leaflet"]')) {
      const linkEl = document.createElement('link');
      linkEl.rel = 'stylesheet';
      linkEl.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(linkEl);
    }
    const existing = document.querySelector('script[src*="leaflet"]');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      if (window.L) resolve();
      return;
    }
    const scriptEl = document.createElement('script');
    scriptEl.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    scriptEl.async = true;
    scriptEl.onload = () => resolve();
    document.head.appendChild(scriptEl);
  });
};

// Post type icons mapping
const POST_TYPE_ICONS = {
  peso: Scale,
  pasto: Utensils,
  esercizio: Dumbbell,
  motivazione: Sparkles,
  traguardo: Trophy
};

// Translations for public post page
const TRANSLATIONS = {
  it: {
    postTypes: {
      peso: 'Aggiornamento Peso',
      pasto: 'Pasto',
      esercizio: 'Allenamento',
      motivazione: 'Motiva',
      traguardo: 'Traguardo'
    },
    time: { now: 'Ora', minsAgo: 'm fa', hoursAgo: 'h fa', daysAgo: 'g fa' },
    register: 'Registrati',
    notFound: 'Post non trovato',
    privatePost: 'Questo post è privato',
    loadError: 'Errore nel caricamento del post',
    connectionError: 'Errore di connessione',
    removedPost: 'Il post potrebbe essere stato rimosso o non è accessibile.',
    enterSlimmy: 'Entra in Slimmy',
    joinSlimmy: 'Unisciti a Slimmy! 🏆',
    joinDescription: 'Traccia il tuo peso, condividi i tuoi progressi e raggiungi i tuoi obiettivi insieme alla community.',
    startFree: 'Inizia Gratis',
    freeNoCard: 'È gratuito • Nessuna carta richiesta',
    trackWeight: 'Traccia Peso',
    groupChallenges: 'Sfide di Gruppo',
    aiRecipes: 'Ricette AI',
    aiTransform: 'Trasformazione AI',
    privacy: 'Privacy',
    terms: 'Termini'
  },
  en: {
    postTypes: {
      peso: 'Weight Update',
      pasto: 'Meal',
      esercizio: 'Workout',
      motivazione: 'Motivation',
      traguardo: 'Milestone'
    },
    time: { now: 'Now', minsAgo: 'm ago', hoursAgo: 'h ago', daysAgo: 'd ago' },
    register: 'Sign Up',
    notFound: 'Post not found',
    privatePost: 'This post is private',
    loadError: 'Error loading post',
    connectionError: 'Connection error',
    removedPost: 'The post may have been removed or is not accessible.',
    enterSlimmy: 'Join Slimmy',
    joinSlimmy: 'Join Slimmy! 🏆',
    joinDescription: 'Track your weight, share your progress and reach your goals together with the community.',
    startFree: 'Start Free',
    freeNoCard: "It's free • No card required",
    trackWeight: 'Track Weight',
    groupChallenges: 'Group Challenges',
    aiRecipes: 'AI Recipes',
    aiTransform: 'AI Transformation',
    privacy: 'Privacy',
    terms: 'Terms'
  },
  es: {
    postTypes: {
      peso: 'Actualización de Peso',
      pasto: 'Comida',
      esercizio: 'Entrenamiento',
      motivazione: 'Motivación',
      traguardo: 'Logro'
    },
    time: { now: 'Ahora', minsAgo: 'm', hoursAgo: 'h', daysAgo: 'd' },
    register: 'Regístrate',
    notFound: 'Publicación no encontrada',
    privatePost: 'Esta publicación es privada',
    loadError: 'Error al cargar la publicación',
    connectionError: 'Error de conexión',
    removedPost: 'La publicación puede haber sido eliminada o no es accesible.',
    enterSlimmy: 'Entra en Slimmy',
    joinSlimmy: '¡Únete a Slimmy! 🏆',
    joinDescription: 'Registra tu peso, comparte tu progreso y alcanza tus metas junto con la comunidad.',
    startFree: 'Empieza Gratis',
    freeNoCard: 'Es gratis • Sin tarjeta',
    trackWeight: 'Registra Peso',
    groupChallenges: 'Retos Grupales',
    aiRecipes: 'Recetas IA',
    aiTransform: 'Transformación IA',
    privacy: 'Privacidad',
    terms: 'Términos'
  },
  pt: {
    postTypes: {
      peso: 'Atualização de Peso',
      pasto: 'Refeição',
      esercizio: 'Treino',
      motivazione: 'Motivação',
      traguardo: 'Conquista'
    },
    time: { now: 'Agora', minsAgo: 'm', hoursAgo: 'h', daysAgo: 'd' },
    register: 'Cadastre-se',
    notFound: 'Publicação não encontrada',
    privatePost: 'Esta publicação é privada',
    loadError: 'Erro ao carregar a publicação',
    connectionError: 'Erro de conexão',
    removedPost: 'A publicação pode ter sido removida ou não está acessível.',
    enterSlimmy: 'Entre no Slimmy',
    joinSlimmy: 'Junte-se ao Slimmy! 🏆',
    joinDescription: 'Acompanhe seu peso, compartilhe seu progresso e alcance seus objetivos junto com a comunidade.',
    startFree: 'Comece Grátis',
    freeNoCard: 'É grátis • Sem cartão',
    trackWeight: 'Rastreie Peso',
    groupChallenges: 'Desafios em Grupo',
    aiRecipes: 'Receitas IA',
    aiTransform: 'Transformação IA',
    privacy: 'Privacidade',
    terms: 'Termos'
  },
  de: {
    postTypes: {
      peso: 'Gewichts-Update',
      pasto: 'Mahlzeit',
      esercizio: 'Training',
      motivazione: 'Motivation',
      traguardo: 'Meilenstein'
    },
    time: { now: 'Jetzt', minsAgo: ' Min', hoursAgo: ' Std', daysAgo: ' T' },
    register: 'Registrieren',
    notFound: 'Beitrag nicht gefunden',
    privatePost: 'Dieser Beitrag ist privat',
    loadError: 'Fehler beim Laden des Beitrags',
    connectionError: 'Verbindungsfehler',
    removedPost: 'Der Beitrag wurde möglicherweise entfernt oder ist nicht zugänglich.',
    enterSlimmy: 'Tritt Slimmy bei',
    joinSlimmy: 'Tritt Slimmy bei! 🏆',
    joinDescription: 'Verfolge dein Gewicht, teile deinen Fortschritt und erreiche deine Ziele gemeinsam mit der Community.',
    startFree: 'Kostenlos starten',
    freeNoCard: 'Kostenlos • Keine Karte erforderlich',
    trackWeight: 'Gewicht tracken',
    groupChallenges: 'Gruppen-Challenges',
    aiRecipes: 'KI-Rezepte',
    aiTransform: 'KI-Transformation',
    privacy: 'Datenschutz',
    terms: 'AGB'
  },
  fr: {
    postTypes: {
      peso: 'Mise à jour du poids',
      pasto: 'Repas',
      esercizio: 'Entraînement',
      motivazione: 'Motivation',
      traguardo: 'Objectif atteint'
    },
    time: { now: 'Maintenant', minsAgo: ' min', hoursAgo: ' h', daysAgo: ' j' },
    register: "S'inscrire",
    notFound: 'Publication non trouvée',
    privatePost: 'Cette publication est privée',
    loadError: 'Erreur lors du chargement',
    connectionError: 'Erreur de connexion',
    removedPost: "La publication a peut-être été supprimée ou n'est pas accessible.",
    enterSlimmy: 'Rejoins Slimmy',
    joinSlimmy: 'Rejoins Slimmy ! 🏆',
    joinDescription: 'Suis ton poids, partage tes progrès et atteins tes objectifs avec la communauté.',
    startFree: 'Commencer gratuitement',
    freeNoCard: 'Gratuit • Aucune carte requise',
    trackWeight: 'Suivi du poids',
    groupChallenges: 'Défis de groupe',
    aiRecipes: 'Recettes IA',
    aiTransform: 'Transformation IA',
    privacy: 'Confidentialité',
    terms: 'Conditions'
  }
};

export default function PublicPost() {
  const location = useLocation();
  // Extract postId and lang from URL
  const postId = location.pathname.split('/post/')[1];
  const searchParams = new URLSearchParams(location.search);
  const urlLang = searchParams.get('lang') || 'it';
  
  // Get translations for the language
  const t = useMemo(() => TRANSLATIONS[urlLang] || TRANSLATIONS.it, [urlLang]);
  
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPost = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/posts/public/${postId}`);
      
      if (!res.ok) {
        if (res.status === 404) {
          setError(t.notFound);
        } else if (res.status === 403) {
          setError(t.privatePost);
        } else {
          setError(t.loadError);
        }
        return;
      }
      
      const data = await res.json();
      setPost(data);
    } catch (err) {
      console.error('Error fetching post:', err);
      setError(t.connectionError);
    } finally {
      setLoading(false);
    }
  }, [postId, t]);

  useEffect(() => {
    if (postId) {
      fetchPost();
    }
  }, [postId, fetchPost]);

  // Load Leaflet lazily once we know the post is a workout with GPS
  // positions — keeps the meal/weight share pages lightweight.
  useEffect(() => {
    if (post?.post_type === 'esercizio' && Array.isArray(post?.data?.positions) && post.data.positions.length > 0) {
      ensureLeafletLoaded();
    }
  }, [post]);

  // Format seconds → HH:MM:SS or MM:SS for workout duration display.
  const formatDuration = (seconds) => {
    const s = Math.max(0, Math.floor(Number(seconds) || 0));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    const pad = (n) => String(n).padStart(2, '0');
    // Rule: < 1h → MM:SS. ≥ 1h → H:MM h (suffisso "h" per chiarezza).
    return h > 0 ? `${h}:${pad(m)} h` : `${m}:${pad(sec)}`;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t.time.now;
    if (diffMins < 60) return `${diffMins}${t.time.minsAgo}`;
    if (diffHours < 24) return `${diffHours}${t.time.hoursAgo}`;
    if (diffDays < 7) return `${diffDays}${t.time.daysAgo}`;
    
    // Use locale for date
    const locale = urlLang === 'en' ? 'en-US' : urlLang === 'de' ? 'de-DE' : urlLang === 'fr' ? 'fr-FR' : urlLang === 'es' ? 'es-ES' : urlLang === 'pt' ? 'pt-BR' : 'it-IT';
    return date.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
  };

  const getTotalReactions = () => {
    if (!post?.reactions) return 0;
    return Object.values(post.reactions).reduce((sum, users) => sum + (users?.length || 0), 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#080D1A] via-[#0a1628] to-[#0d1f3c] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E8334A]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#080D1A] via-[#0a1628] to-[#0d1f3c] flex flex-col items-center justify-center p-6">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
            <span className="text-4xl">😕</span>
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">{error}</h1>
          <p className="text-gray-400 mb-8">{t.removedPost}</p>
          <a 
            href="https://slimmy.app"
            className="inline-flex items-center gap-2 bg-[#E8334A] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#d62d43] transition-colors"
          >
            {t.enterSlimmy}
            <ChevronRight className="w-5 h-5" />
          </a>
        </div>
      </div>
    );
  }

  const PostIcon = POST_TYPE_ICONS[post?.post_type] || Sparkles;
  const postTypeLabel = t.postTypes[post?.post_type] || 'Post';

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#080D1A] via-[#0a1628] to-[#0d1f3c]" style={{ paddingBottom: 'calc(24px + env(safe-area-inset-bottom, 0px))' }}>
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#080D1A]/80 border-b border-white/10" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <a href="https://slimmy.app" className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_57e2e740-274d-4480-9155-167f0e5dcf96/artifacts/q2sfm1zu_logo_white.png" 
              alt="Slimmy" 
              className="h-7"
            />
          </a>
          <a 
            href="https://slimmy.app"
            className="bg-[#E8334A] text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-[#d62d43] transition-colors"
            data-testid="header-register-btn"
          >
            {t.register}
          </a>
        </div>
      </header>

      {/* Post Content */}
      <main className="max-w-lg mx-auto px-3 pt-3 pb-2">
        <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden">
          {/* Author Header */}
          <div className="p-3 flex items-center gap-3">
            {post.author_picture ? (
              <img 
                src={post.author_picture} 
                alt={post.author_name || post.user_name}
                className="w-10 h-10 rounded-full object-cover border-2 border-[#E8334A]/30"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#E8334A] to-[#f97316] flex items-center justify-center text-white font-bold">
                {(post.author_name || post.user_name || 'U')[0].toUpperCase()}
              </div>
            )}
            <div className="flex-1">
              <p className="text-white font-semibold text-sm">{post.author_name || post.user_name}</p>
              <div className="flex items-center gap-2 text-gray-400 text-xs">
                <PostIcon className="w-3 h-3" />
                <span>{postTypeLabel}</span>
                <span>•</span>
                <span>{formatDate(post.created_at)}</span>
              </div>
            </div>
          </div>

          {/* Post Text */}
          <div className="px-3 pb-2">
            <p className="text-white/90 text-sm whitespace-pre-wrap select-text line-clamp-2">{post.text}</p>
          </div>

          {/* Post Image — supports multi-image carousel with URL prefix for the public shared view */}
          {(post.image_url || (Array.isArray(post.image_urls) && post.image_urls.length > 0)) && (
            <PostImage
              post={post}
              maxHeightClass="max-h-[280px]"
              urlTransform={(u) => (u && u.startsWith('/api') ? `${API_URL}${u}` : u)}
            />
          )}

          {/* Workout map + stats — shown for `esercizio` posts that
              carry GPS positions. Mirrors the in-app feed rendering so
              the shared link gives the recipient the full context of
              the workout (route, distance, time, calories) instead of
              a bare caption. */}
          {post.post_type === 'esercizio' && Array.isArray(post?.data?.positions) && post.data.positions.length > 1 && (
            <div className="relative">
              <RouteMap positions={post.data.positions} height={220} />
            </div>
          )}
          {post.post_type === 'esercizio' && post?.data && (
            <div className="px-3 py-3 grid grid-cols-3 gap-2 border-t border-white/10 bg-white/5">
              <div className="flex flex-col items-center text-center">
                <Navigation className="w-4 h-4 text-[#22D3EE] mb-1" />
                <span className="text-white text-base font-bold">
                  {(Number(post.data.distance) || 0).toFixed(2)}
                </span>
                <span className="text-white/60 text-[10px] uppercase tracking-wide">km</span>
              </div>
              <div className="flex flex-col items-center text-center">
                <Clock className="w-4 h-4 text-[#16A34A] mb-1" />
                <span className="text-white text-base font-bold">
                  {formatDuration(post.data.duration_seconds || post.data.duration || 0)}
                </span>
                <span className="text-white/60 text-[10px] uppercase tracking-wide">
                  {t.duration || 'tempo'}
                </span>
              </div>
              <div className="flex flex-col items-center text-center">
                <Flame className="w-4 h-4 text-[#F59E0B] mb-1" />
                <span className="text-white text-base font-bold">
                  {Math.round(Number(post.data.calories) || 0)}
                </span>
                <span className="text-white/60 text-[10px] uppercase tracking-wide">kcal</span>
              </div>
            </div>
          )}

          {/* Reactions & Comments Count */}
          <div className="px-3 py-2 flex items-center gap-4 border-t border-white/10">
            <div className="flex items-center gap-1.5 text-gray-400 text-sm">
              <Heart className="w-4 h-4" />
              <span>{getTotalReactions()}</span>
            </div>
            <div className="flex items-center gap-1.5 text-gray-400 text-sm">
              <MessageCircle className="w-4 h-4" />
              <span>{post.comments?.length || 0}</span>
            </div>
          </div>
        </div>

        {/* CTA Card - Compact */}
        <div className="mt-3 bg-gradient-to-r from-[#E8334A]/20 to-[#f97316]/20 backdrop-blur-xl rounded-2xl border border-[#E8334A]/30 p-3 text-center">
          <h2 className="text-base font-bold text-white mb-1">
            {t.joinSlimmy}
          </h2>
          <p className="text-gray-300 text-xs mb-2 line-clamp-2">
            {t.joinDescription}
          </p>
          <a 
            href="https://slimmy.app"
            className="inline-flex items-center gap-2 bg-[#E8334A] text-white px-5 py-2.5 rounded-xl font-bold text-sm hover:bg-[#d62d43] transition-all shadow-lg shadow-[#E8334A]/30"
            data-testid="cta-register-btn"
          >
            {t.startFree}
            <ChevronRight className="w-4 h-4" />
          </a>
        </div>
      </main>
    </div>
  );
}
